import React, { useState } from 'react';
import {
  Menu,
  MenuItem,
  ListItemIcon,
  Box,
  Typography,
  Avatar,
  Divider,
  useTheme
} from '@mui/material';
import {
  OpenInNew,
  Brightness4,
  Brightness7,
  Logout,
  Download,
  Code
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { HelpDialog, ShortcutsDialog } from './HelpDialogs';
import { AdminPanelSettings } from '@mui/icons-material';

const isElectron = typeof window !== 'undefined' && window.electronAPI?.isElectron === true;

const ProfileMenu = ({ anchorEl, handleClose, handleLogout, user, toggleColorMode, mode }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  
  const [helpOpen, setHelpOpen] = useState(false);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);

  return (
    <>
      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        onClick={handleClose}
      PaperProps={{
        elevation: 0,
        sx: {
          filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
          mt: 1.5,
          width: 320,
          bgcolor: 'background.paper',
          border: `1px solid ${theme.palette.divider}`,
          '& .MuiAvatar-root': {
            width: 32,
            height: 32,
            ml: -0.5,
            mr: 1,
          },
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      {/* HEADER: ACCOUNT */}
      <Box sx={{ px: 2, py: 2 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem', color: 'text.secondary', display: 'block', mb: 1.5, letterSpacing: '0.05em' }}>
          COMPTE
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Avatar 
            src={user?.avatar} 
            alt={user?.name}
            sx={{ width: 40, height: 40, mr: 1.5, bgcolor: 'primary.main', fontSize: '1.2rem' }}
          >
            {user?.name?.charAt(0).toUpperCase()}
          </Avatar>
          <Box sx={{ overflow: 'hidden' }}>
            <Typography variant="subtitle2" noWrap sx={{ fontWeight: 600, fontSize: '0.95rem' }}>
              {user?.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontSize: '0.8rem' }}>
              {user?.email || 'user@example.com'}
            </Typography>
          </Box>
        </Box>
      </Box>

      <MenuItem onClick={() => navigate('/profile', { state: { tab: 0 } })} sx={{ py: 1 }}>
        <Typography variant="body2" sx={{ flexGrow: 1 }}>Gérer le compte</Typography>
        <OpenInNew fontSize="small" sx={{ color: 'text.secondary', fontSize: '1rem' }} />
      </MenuItem>

      <Divider sx={{ my: 1 }} />

      {/* SECTION: ERA */}
      <Box sx={{ px: 2, py: 1 }}>
        <Typography variant="caption" sx={{ fontWeight: 'bold', fontSize: '0.7rem', color: 'text.secondary', letterSpacing: '0.05em' }}>
          ERA
        </Typography>
      </Box>

      <MenuItem onClick={() => navigate('/profile', { state: { tab: 2 } })} sx={{ py: 1 }}>
        <Typography variant="body2">Profil et visibilité</Typography>
      </MenuItem>
      <MenuItem sx={{ py: 1 }}>
        <Typography variant="body2">Activité</Typography>
      </MenuItem>
      <MenuItem onClick={() => navigate('/portfolio')} sx={{ py: 1 }}>
        <Typography variant='body2'>Portfolio</Typography>
      </MenuItem>
      <MenuItem onClick={() => navigate('/reports')} sx={{ py: 1 }}>
        <Typography variant='body2'>Rapports</Typography>
      </MenuItem>
      <MenuItem onClick={() => navigate('/analytics')} sx={{ py: 1 }}>
        <Typography variant='body2'>Analytics</Typography>
      </MenuItem>
      <MenuItem onClick={() => navigate('/calendar')} sx={{ py: 1 }}>
        <Typography variant='body2'>Calendrier</Typography>
      </MenuItem>
      <MenuItem onClick={() => navigate('/my-cards')} sx={{ py: 1 }}>
        <Typography variant="body2">Cartes</Typography>
      </MenuItem>
      <MenuItem onClick={() => navigate('/helpdesk')} sx={{ py: 1 }}>
        <Typography variant='body2'>Support / Aide</Typography>
      </MenuItem>
      <MenuItem onClick={() => navigate('/settings')} sx={{ py: 1 }}>
        <Typography variant="body2">Paramètres</Typography>
      </MenuItem>
      <MenuItem onClick={() => navigate('/developers')} sx={{ py: 1 }}>
        <ListItemIcon><Code fontSize='small' /></ListItemIcon>
        <Typography variant="body2">Portail développeur</Typography>
      </MenuItem>
      {!isElectron && user?.role === 'admin' && (
      <MenuItem onClick={() => navigate('/admin')} sx={{ py: 1 }}>
        <ListItemIcon><AdminPanelSettings fontSize='small' color='error' /></ListItemIcon>
        <Typography variant='body2' color='error.main' fontWeight='bold'>Administration</Typography>
      </MenuItem>
      )}
      {!isElectron && (
        <MenuItem onClick={() => navigate('/subscription')} sx={{ py: 1 }}>
          <Typography variant="body2" color="primary.main" fontWeight="bold">💎 Abonnement Premium</Typography>
        </MenuItem>
      )}
      {!isElectron && (
        <MenuItem
          component="a"
          href={`${process.env.REACT_APP_API_URL?.replace('/api', '') || ''}/api/download/desktop`}
          download
          sx={{ py: 1 }}
        >
          <ListItemIcon><Download fontSize="small" /></ListItemIcon>
          <Typography variant="body2">Télécharger l'app desktop</Typography>
        </MenuItem>
      )}
      
      <MenuItem onClick={(e) => { e.stopPropagation(); toggleColorMode(); }} sx={{ py: 1 }}>
        <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
          Thème
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary' }}>
            {mode === 'dark' ? <Brightness7 fontSize="small" /> : <Brightness4 fontSize="small" />}
          </Box>
        </Typography>
      </MenuItem>

      <Divider sx={{ my: 1 }} />

      <MenuItem onClick={() => navigate('/documentation')} sx={{ py: 1 }}>
        <Typography variant="body2">Documentation</Typography>
      </MenuItem>
      <MenuItem onClick={(e) => { e.stopPropagation(); setHelpOpen(true); }} sx={{ py: 1 }}>
        <Typography variant="body2">Aide</Typography>
      </MenuItem>
      <MenuItem onClick={(e) => { e.stopPropagation(); setShortcutsOpen(true); }} sx={{ py: 1 }}>
        <Typography variant="body2">Raccourcis</Typography>
      </MenuItem>

      <Divider sx={{ my: 1 }} />

      <MenuItem onClick={handleLogout} sx={{ py: 1.5 }}>
        <ListItemIcon>
          <Logout fontSize="small" color="error" />
        </ListItemIcon>
        <Typography variant="body2" color="error">Se déconnecter</Typography>
      </MenuItem>
    </Menu>

    <HelpDialog open={helpOpen} onClose={() => setHelpOpen(false)} />
    <ShortcutsDialog open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </>
  );
};

export default ProfileMenu;
